/* ============================================================
   Section Field Definitions — PRD §8.1 CMS
   Type-safe map of every editable field per section.
   Content-backed sections use `fields` (stored in site_content).
   Entity-backed sections use `entityType` (stylists, services, etc.).
   ============================================================ */

export type FieldType = 'text' | 'textarea' | 'url' | 'email' | 'image' | 'html'

export interface SectionField {
  key: string
  label: string
  type: FieldType
  placeholder?: string
  maxLength?: number
}

export interface SectionFieldDef {
  key: string
  label: string
  icon: string
  description: string
  fields?: SectionField[]
  entityType?: string
}

export const SECTION_FIELDS: SectionFieldDef[] = [
  {
    key: 'header',
    label: 'Header',
    icon: '🧭',
    description: 'Site navigation and branding',
    fields: [
      { key: 'logo_text', label: 'Logo Text', type: 'text', placeholder: 'Your Salon Name', maxLength: 40 },
      { key: 'tagline', label: 'Tagline', type: 'text', placeholder: 'Where art meets hair', maxLength: 60 },
      { key: 'nav_1_label', label: 'Nav Link 1 — Label', type: 'text', placeholder: 'Services', maxLength: 30 },
      { key: 'nav_1_route', label: 'Nav Link 1 — Route', type: 'url', placeholder: '#services' },
      { key: 'nav_2_label', label: 'Nav Link 2 — Label', type: 'text', placeholder: 'About', maxLength: 30 },
      { key: 'nav_2_route', label: 'Nav Link 2 — Route', type: 'url', placeholder: '#about' },
      { key: 'nav_3_label', label: 'Nav Link 3 — Label', type: 'text', placeholder: 'Gallery', maxLength: 30 },
      { key: 'nav_3_route', label: 'Nav Link 3 — Route', type: 'url', placeholder: '#gallery' },
      { key: 'nav_4_label', label: 'Nav Link 4 — Label', type: 'text', placeholder: 'Team', maxLength: 30 },
      { key: 'nav_4_route', label: 'Nav Link 4 — Route', type: 'url', placeholder: '#team' },
      { key: 'nav_5_label', label: 'Nav Link 5 — Label', type: 'text', placeholder: 'Contact', maxLength: 30 },
      { key: 'nav_5_route', label: 'Nav Link 5 — Route', type: 'url', placeholder: '#contact' },
      { key: 'nav_6_label', label: 'Nav Link 6 — Label', type: 'text', placeholder: '', maxLength: 30 },
      { key: 'nav_6_route', label: 'Nav Link 6 — Route', type: 'url', placeholder: '' },
      { key: 'cta_text', label: 'Header CTA Text', type: 'text', placeholder: 'Book Now', maxLength: 30 },
      { key: 'cta_link', label: 'Header CTA Link', type: 'url', placeholder: '/book' },
    ],
  },
  {
    key: 'hero',
    label: 'Hero',
    icon: '🎯',
    description: 'Main banner with headline and CTA',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', placeholder: 'Experience The Glow', maxLength: 40 },
      { key: 'headline', label: 'Headline', type: 'text', placeholder: 'Your salon name', maxLength: 80 },
      { key: 'subline', label: 'Subline', type: 'text', placeholder: 'Where structural cuts meet vivid color…', maxLength: 180 },
      { key: 'cta_text', label: 'CTA Button Text', type: 'text', placeholder: 'Book Now', maxLength: 30 },
      { key: 'cta_link', label: 'CTA Link', type: 'url', placeholder: '#services' },
      { key: 'secondary_cta_text', label: 'Secondary CTA Text', type: 'text', placeholder: 'Our Vision', maxLength: 30 },
      { key: 'secondary_cta_link', label: 'Secondary CTA Link', type: 'url', placeholder: '#vision' },
      { key: 'background_image', label: 'Hero Image', type: 'image', placeholder: 'Upload or paste image URL' },
    ],
  },
  {
    key: 'about',
    label: 'About',
    icon: '📖',
    description: 'About your business',
    fields: [
      { key: 'philosophy_label', label: 'Philosophy Label', type: 'text', placeholder: 'Our Philosophy', maxLength: 40 },
      { key: 'philosophy_title', label: 'Philosophy Title', type: 'text', placeholder: 'We believe your hair is the ultimate form of self-expression.', maxLength: 140 },
      { key: 'philosophy_body', label: 'Philosophy Body', type: 'textarea', placeholder: 'Describe your point of view…', maxLength: 1200 },
      { key: 'philosophy_image_url', label: 'Philosophy Image', type: 'image', placeholder: 'Upload or paste image URL' },
      { key: 'philosophy_feature_1_title', label: 'Feature 1 Title', type: 'text', placeholder: '01. Technical Excellence', maxLength: 60 },
      { key: 'philosophy_feature_1_body', label: 'Feature 1 Body', type: 'textarea', placeholder: 'Describe the first differentiator…', maxLength: 240 },
      { key: 'philosophy_feature_2_title', label: 'Feature 2 Title', type: 'text', placeholder: '02. Personalized Craft', maxLength: 60 },
      { key: 'philosophy_feature_2_body', label: 'Feature 2 Body', type: 'textarea', placeholder: 'Describe the second differentiator…', maxLength: 240 },
      { key: 'founder_label', label: 'Founder Label', type: 'text', placeholder: 'The Founder', maxLength: 40 },
      { key: 'founder_name', label: 'Founder Name', type: 'text', placeholder: 'Your name', maxLength: 80 },
      { key: 'founder_quote', label: 'Founder Quote', type: 'textarea', placeholder: '"A sanctuary for the bold and the beautifully specific."', maxLength: 240 },
      { key: 'founder_story_lead', label: 'Founder Story Lead', type: 'textarea', placeholder: 'Add the opening line of your founder story…', maxLength: 400 },
      { key: 'founder_story_body', label: 'Founder Story Body', type: 'textarea', placeholder: 'Add the supporting founder story…', maxLength: 1200 },
      { key: 'founder_image_url', label: 'Founder Image', type: 'image', placeholder: 'Upload or paste image URL' },
      { key: 'approach_label', label: 'Approach Label', type: 'text', placeholder: 'Our Approach', maxLength: 40 },
      { key: 'approach_intro', label: 'Approach Intro', type: 'textarea', placeholder: 'Introduce how your process works…', maxLength: 400 },
      { key: 'approach_step_1_title', label: 'Step 1 Title', type: 'text', placeholder: 'Consultation', maxLength: 40 },
      { key: 'approach_step_1_body', label: 'Step 1 Body', type: 'textarea', placeholder: 'Explain your consultation step…', maxLength: 240 },
      { key: 'approach_step_2_title', label: 'Step 2 Title', type: 'text', placeholder: 'Creation', maxLength: 40 },
      { key: 'approach_step_2_body', label: 'Step 2 Body', type: 'textarea', placeholder: 'Explain your creation step…', maxLength: 240 },
      { key: 'approach_step_3_title', label: 'Step 3 Title', type: 'text', placeholder: 'Transformation', maxLength: 40 },
      { key: 'approach_step_3_body', label: 'Step 3 Body', type: 'textarea', placeholder: 'Explain your transformation step…', maxLength: 240 },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    icon: '✂️',
    description: 'Service menu and pricing',
    entityType: 'services',
  },
  {
    key: 'team',
    label: 'Team',
    icon: '👥',
    description: 'Your stylists and staff',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', placeholder: 'The Creators', maxLength: 40 },
      { key: 'title', label: 'Section Title', type: 'text', placeholder: 'Meet the artists behind the chair.', maxLength: 100 },
      { key: 'intro', label: 'Intro', type: 'textarea', placeholder: 'Introduce your team section…', maxLength: 400 },
      { key: 'cta_text', label: 'CTA Text', type: 'text', placeholder: 'View Full Team', maxLength: 30 },
      { key: 'cta_link', label: 'CTA Link', type: 'url', placeholder: '#team' },
    ],
    entityType: 'stylists',
  },
  {
    key: 'testimonials',
    label: 'Testimonials',
    icon: '⭐',
    description: 'Client reviews and feedback',
    entityType: 'testimonials',
  },
  {
    key: 'contact',
    label: 'Contact',
    icon: '📍',
    description: 'Location, hours, and contact info',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', placeholder: 'Ready for change?', maxLength: 40 },
      { key: 'headline', label: 'Headline', type: 'text', placeholder: 'Book your session.', maxLength: 80 },
      { key: 'body', label: 'Body Text', type: 'textarea', placeholder: 'Tell clients what to expect…', maxLength: 600 },
      { key: 'cta_text', label: 'CTA Button Text', type: 'text', placeholder: 'Request Appointment', maxLength: 30 },
      { key: 'cta_link', label: 'CTA Link', type: 'url', placeholder: '/your-salon/book' },
      { key: 'phone', label: 'Phone', type: 'text', placeholder: '(555) 123-4567' },
      { key: 'email', label: 'Email', type: 'email', placeholder: 'hello@yoursalon.com' },
      { key: 'address', label: 'Address', type: 'textarea', placeholder: '123 Main St\nCity, ST 12345', maxLength: 300 },
      { key: 'instagram', label: 'Instagram Handle', type: 'text', placeholder: '@yoursalon' },
      { key: 'hours', label: 'Hours', type: 'textarea', placeholder: 'Tue - Fri: 10am - 8pm\nSaturday: 9am - 6pm', maxLength: 300 },
      { key: 'map_embed', label: 'Google Maps Embed', type: 'html', placeholder: '<iframe src="..." />' },
    ],
  },
  {
    key: 'gallery',
    label: 'Gallery',
    icon: '🖼️',
    description: 'Photo gallery showcase',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text', placeholder: 'The Gallery', maxLength: 40 },
      { key: 'title', label: 'Section Title', type: 'text', placeholder: 'Work, process, and atmosphere.', maxLength: 80 },
      { key: 'intro', label: 'Intro', type: 'textarea', placeholder: 'Introduce your gallery…', maxLength: 300 },
    ],
  },
  {
    key: 'events',
    label: 'Events',
    icon: '📅',
    description: 'Upcoming events and promotions',
    entityType: 'events',
  },
  {
    key: 'faq',
    label: 'FAQ',
    icon: '❓',
    description: 'Frequently asked questions',
    fields: [
      { key: 'title', label: 'Section Title', type: 'text', placeholder: 'FAQ', maxLength: 60 },
    ],
  },
  {
    key: 'cta',
    label: 'Call to Action',
    icon: '📣',
    description: 'Conversion-focused banner',
    fields: [
      { key: 'headline', label: 'Headline', type: 'text', placeholder: 'Ready to transform?', maxLength: 80 },
      { key: 'body', label: 'Body Text', type: 'textarea', placeholder: 'Book your appointment today...', maxLength: 500 },
      { key: 'cta_text', label: 'Button Text', type: 'text', placeholder: 'Get Started', maxLength: 30 },
      { key: 'cta_link', label: 'Button Link', type: 'url', placeholder: '/booking' },
    ],
  },
  {
    key: 'footer',
    label: 'Footer',
    icon: '🦶',
    description: 'Site footer with links and social',
    fields: [
      { key: 'tagline', label: 'Footer Tagline', type: 'text', placeholder: 'Crafted with intention.', maxLength: 80 },
      { key: 'copyright', label: 'Copyright Text', type: 'text', placeholder: '© 2026 Your Salon. All rights reserved.', maxLength: 100 },
      { key: 'nav_1_label', label: 'Footer Link 1 — Label', type: 'text', placeholder: 'Services', maxLength: 30 },
      { key: 'nav_1_route', label: 'Footer Link 1 — Route', type: 'url', placeholder: '#services' },
      { key: 'nav_2_label', label: 'Footer Link 2 — Label', type: 'text', placeholder: 'About', maxLength: 30 },
      { key: 'nav_2_route', label: 'Footer Link 2 — Route', type: 'url', placeholder: '#about' },
      { key: 'nav_3_label', label: 'Footer Link 3 — Label', type: 'text', placeholder: 'Gallery', maxLength: 30 },
      { key: 'nav_3_route', label: 'Footer Link 3 — Route', type: 'url', placeholder: '#gallery' },
      { key: 'nav_4_label', label: 'Footer Link 4 — Label', type: 'text', placeholder: 'Contact', maxLength: 30 },
      { key: 'nav_4_route', label: 'Footer Link 4 — Route', type: 'url', placeholder: '#contact' },
      { key: 'instagram', label: 'Instagram', type: 'text', placeholder: '@yoursalon' },
      { key: 'tiktok', label: 'TikTok', type: 'text', placeholder: '@yoursalon' },
      { key: 'facebook', label: 'Facebook', type: 'url', placeholder: 'https://facebook.com/yoursalon' },
      { key: 'privacy_link', label: 'Privacy Policy Link', type: 'url', placeholder: '/privacy' },
      { key: 'terms_link', label: 'Terms of Service Link', type: 'url', placeholder: '/terms' },
    ],
  },
]

/** Lookup a section definition by key */
export function getSectionDef(sectionKey: string): SectionFieldDef | undefined {
  return SECTION_FIELDS.find((s) => s.key === sectionKey)
}

/** Check if a section is entity-backed (managed via separate table) vs content-backed */
export function isEntitySection(sectionKey: string): boolean {
  const def = getSectionDef(sectionKey)
  return !!def?.entityType
}
